<template>
  <section class="section">
    <div class="container">
      <h1 class="title">{{ $translate(materialtext) | capitalize }}</h1>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <b-button
              v-if="canManage"
              type="is-primary"
              icon-right="plus"
              @click="
                $router.push({
                  name: 'newMaterial',
                  params: { material: materialtext, id: -1, module: module },
                })
              "
            ></b-button>
          </div>
        </div>
        <div class="level-right">
          <b-field>
            <b-input
              :placeholder="$translate('search').concat('...') | capitalize"
              type="search"
              v-model="searchname"
              icon="magnify"
              @input="searchData()"
            ></b-input>
          </b-field>
        </div>
      </div>
      <b-table
        :data="filteredData"
        :paginated="isPaginated"
        :per-page="perPage"
        :current-page.sync="currentPage"
        :pagination-simple="isPaginationSimple"
        :pagination-position="paginationPosition"
        :default-sort-direction="defaultSortDirection"
        :sort-icon="sortIcon"
        :sort-icon-size="sortIconSize"
        aria-next-label="Next page"
        aria-previous-label="Previous page"
        aria-page-label="Page"
        aria-current-label="Current page"
        checkable
        detailed
        :default-sort="[sortField, sortOrder]"
        backend-sorting
        @sort="sortPressed"
      >
        <template slot-scope="props">
          <b-table-column field="CUI" :label="$translate('CUI') | capitalize" sortable>
            {{ props.row.CUI }}
          </b-table-column>
          <b-table-column field="commercial_name" :label="$translate('commercial_name') | capitalize" sortable>
            {{ $translateMaterial(props.row) }}
          </b-table-column>
          <b-table-column field="thickness" :label="$translate('thickness') | capitalize">
            {{ props.row.thickness }}
          </b-table-column>
          <b-table-column field="hardness" :label="$translate('hardness') | capitalize">
            {{ props.row.hardness }}
          </b-table-column>
          <b-table-column field="optional_tag" :label="$translate('optional_tag') | capitalize">
            <template v-for="item in props.row.optional_tag">
              {{ item.key }}
            </template></b-table-column
          >
          <b-table-column :label="$translate('action') | capitalize">
            <a
              v-if="canManage"
              @click="
                $router.push({
                  name: 'editMaterial',
                  params: { material: materialtext, id: props.row.id, module: module },
                })
              "
            >
              <b-icon icon="pencil-outline"></b-icon>
            </a>
            <a @click="askForModels(props.row.id)">
              <b-icon icon="format-list-checkbox"></b-icon>
            </a>
            <a v-if="canManage" @click="copymodal(props.row.id)">
              <b-icon icon="content-copy"></b-icon>
            </a>
            <a v-if="canManage" @click="deleteOccurence(props.row.id, props.row.variants)">
              <b-icon icon="delete-outline"></b-icon>
            </a>
          </b-table-column>
        </template>
        <template slot="detail" slot-scope="props">
          <article v-for="variant in props.row.variants" v-bind:key="variant.id" class="media">
            <figure class="media-left">
              <p class="image">
                <img v-bind:src="variant.image" />
              </p>
            </figure>
            <div v-if="hasVariants" class="media-content">
              <div class="content">
                <p>
                  <strong>{{ $translateMaterialVariant(variant) }}</strong
                  ><br />
                  {{ variant.id }} - {{ variant.CUIV }}
                  <br />
                  CUI:
                  <template v-if="variant.CUI_optionalbool">{{ variant.CUI_optional }}</template
                  ><template v-else>{{ props.row.CUI }}</template>
                </p>
              </div>
            </div>
          </article>
        </template>
      </b-table>
    </div>

    <b-modal :active.sync="isComponentModalActive" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <div class="modal-card" style="width: auto;">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ $translate('copy_material') | capitalize }}
          </p>
        </header>
        <section class="modal-card-body">
          <b-field :label="$translate('material') | capitalize" horizontal>
            <b-select name="material" v-model="tempmodel">
              <template v-for="mat in $availableMaterials[module]">
                <option :key="mat" :value="mat" v-if="mat != materialtext">
                  {{ $translate(mat) | capitalize }}
                </option>
              </template>
            </b-select>
          </b-field>
        </section>
        <footer class="modal-card-foot">
          <button class="button" type="button" @click="isComponentModalActive = false">
            {{ $translate('undo') | capitalize }}
          </button>
          <button type="button" class="button is-primary" @click="copyfunction">
            {{ $translate('copy_material_button') | capitalize }}
          </button>
        </footer>
      </div>
    </b-modal>

    <b-modal :active.sync="isComponentModalModelListActive" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <div class="modal-card" style="width: auto;">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ $translate('models') | capitalize }}
          </p>
        </header>
        <section class="modal-card-body">
          <ul>
            <li v-for="model in modelList" :key="model.id">
              {{ model.name }}
            </li>
          </ul>
        </section>
        <footer class="modal-card-foot">
          <button class="button" type="button" @click="isComponentModalModelListActive = false">
            {{ $translate('close') | capitalize }}
          </button>
        </footer>
      </div>
    </b-modal>

    <b-loading :is-full-page="true" :active.sync="isSaveDisabled" :can-cancel="false"></b-loading>
  </section>
</template>

<script>
import materialTools from '../../../helpers/mixins/materialTools'

export default {
  name: 'GeneralTableMaterial',
  mixins: [materialTools],
  props: {
    materialtext: {},
    module: {},
    canManage: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    hasVariants() {
      return !this.$moduleConfiguration[this.module].materials.noVariants.includes(this.materialtext)
    },
  },
  data() {
    return {
      searchname: null,
      tempmodel: null,
      isComponentModalActive: false,
      isComponentModalModelListActive: false,
      modelList: [],
      material: {
        id: 0,
        CUI: '',
        commercial_name: '',
        thickness: 0,
        hardness: 0,
        optional_tag: '',
      },
      tableData: [],
      filteredData: [],
      isSaveDisabled: false,
      page: 1,
      total: 0,
      sortField: 'commercial_name',
      sortOrder: 'asc',
      defaultSortOrder: 'asc',
      checkedRows: [],
      queryString: '',

      isPaginated: true,
      isPaginationSimple: false,
      paginationPosition: 'bottom',
      defaultSortDirection: 'asc',
      sortIcon: 'arrow-up',
      sortIconSize: 'is-small',
      currentPage: 1,
      perPage: 10,
      idtocopy: null,
    }
  },
  mounted() {
    this.loadAsyncData()
  },
  methods: {
    sortPressed(field, order) {
      this.sortField = field
      this.sortOrder = order
      if (this.sortField === 'commercial_name') {
        this.filteredData.sort((a, b) => this.compareCommercialName(a, b))
      } else {
        this.filteredData.sort((a, b) => this.compareField(a, b))
      }
      if (this.sortOrder === 'desc') {
        this.filteredData.reverse()
      }
    },
    searchData() {
      this.filteredData = []
      this.tableData.forEach(material => {
        if (
          this.searchname == null ||
          this.$translateMaterial(material)
            .toLowerCase()
            .includes(this.searchname.toLowerCase()) ||
          material.CUI.toLowerCase().includes(this.searchname.toLowerCase()) ||
          material.optional_tag
            .map(e => e.key)
            .join(' ')
            .toLowerCase()
            .includes(this.searchname.toLowerCase())
        ) {
          this.filteredData.push(material)
        }
      })
    },
    copyfunction() {
      if (this.tempmodel != null) {
        this.$router.push({
          name: 'newMaterial',
          params: {
            material: this.tempmodel,
            idtocopy: this.idtocopy,
            materialtocopy: this.materialtext,
            module: this.module,
            id: -1,
          },
        })
      } else {
        this.$buefy.notification.open({
          duration: 3000,
          message: this.$translate('failed_form'),
          type: 'is-danger',
        })
      }
    },
    async askForModels(idtorequest) {
      const moduleConfig = this.$moduleConfiguration[this.module]
      this.isSaveDisabled = true
      this.modelList = []
      const modelsTemp = await this.getModelListByMaterial(idtorequest, this.materialtext, moduleConfig)
      this.modelList = [...modelsTemp]
      this.isComponentModalModelListActive = true
      this.isSaveDisabled = false
    },
    copymodal(idtocopy) {
      this.idtocopy = idtocopy
      this.isComponentModalActive = true
    },
    deleteOccurence(idtoerase, variants) {
      this.$buefy.dialog.confirm({
        title: this.$translate('attention'),
        message: this.$translate('material_message_destroy'),
        cancelText: this.$translate('undo'),
        confirmText: this.$translate('material_message_confirm'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () =>
          this.$db
            .collection(this.materialtext)
            .doc(idtoerase)
            .delete()
            .then(async () => {
              for (const key in variants) {
                var desertRef = this.$storage.ref(
                  'materials/variants/' + this.materialtext + '/' + idtoerase + '/' + key + '/key'
                )
                await desertRef
                  .delete()
                  .then(function() {})
                  .catch() //function(error) {
                //console.log(error);
                //});
              }
              this.loadAsyncData()
              this.$buefy.notification.open({
                duration: 3000,
                message: this.$translate('success_delete'),
              })
            }),
      })
    },

    loadAsyncData() {
      this.getMaterialArray(this.materialtext)
        .then(arrayresponse => {
          this.tableData = arrayresponse
          this.tableData.forEach(row => {
            if (!Array.isArray(row.optional_tag)) {
              //console.log(this.materialtext);
              //console.log(row.commercial_name);
            }
          })

          this.filteredData = this.tableData
          this.sortPressed('commercial_name', 'asc')
        })
        .catch((this.tableData = []))
    },
  },
}
</script>

<style scoped>
.container >>> p.image img {
  height: auto;
  width: 100px;
}
.container >>> thead th:nth-child(9),
.container >>> thead th:nth-child(10),
.container >>> thead th:nth-child(11),
.container >>> thead th:nth-child(12),
.container >>> thead th:nth-child(13) {
  display: none;
}
</style>
